<template>
    <div class="overflow-hidden rounded-lg border border-gray-200">
        <!-- Header -->
        <button
            type="button"
            class="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-100"
            @click="expandedSection = !expandedSection"
        >
            <div class="flex items-center space-x-2">
                <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100">
                    <font-awesome-icon icon="fa-solid fa-layer-group" class="text-xs text-violet-600" />
                </span>
                <span class="text-sm font-semibold text-gray-700">Faixas de Taxas por Valor</span>
                <span class="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                    {{ tiers.length }} {{ tiers.length === 1 ? 'faixa' : 'faixas' }}
                </span>
            </div>
            <div class="flex items-center gap-2">
                <span
                    class="inline-flex items-center rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-indigo-600 disabled:cursor-not-allowed disabled:bg-indigo-300"
                    @click.stop="openModal()"
                >
                    <font-awesome-icon icon="fa-solid fa-plus" class="mr-1" />
                    Nova Faixa
                </span>
                <font-awesome-icon
                    icon="fa-solid fa-chevron-down"
                    class="text-xs text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': expandedSection }"
                />
            </div>
        </button>

        <div v-show="expandedSection" class="border-t border-gray-200">

        <ticto-loading v-if="loading" class="p-4" />

        <div v-else-if="tiers.length === 0" class="p-4">
            <p class="text-sm text-gray-500 dark:text-gray-400">
                Nenhuma faixa de taxa configurada. As taxas padrão do seller
                serão utilizadas para todos os valores.
            </p>
        </div>

        <div v-else class="space-y-3 p-4">
            <div
                v-for="tier in tiers"
                :key="tier.id"
                class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
            >
                <!-- Header — always visible -->
                <div
                    class="flex cursor-pointer items-center justify-between bg-gray-50 px-4 py-3 dark:bg-gray-800/80"
                    @click="toggleTier(tier.id)"
                >
                    <div class="flex items-center gap-3">
                        <font-awesome-icon
                            icon="fa-solid fa-chevron-down"
                            class="w-3 text-xs text-gray-400 transition-transform duration-200"
                            :class="{ 'rotate-180': !expandedTiers[tier.id] }"
                        />
                        <span class="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                            {{ formatCurrency(tier.min_amount) }} — {{ formatCurrency(tier.max_amount) }}
                        </span>
                        <!-- Compact summary when collapsed -->
                        <div v-if="!expandedTiers[tier.id]" class="hidden items-center gap-2 text-xs text-gray-400 sm:flex">
                            <span>MDR {{ formatFee(tier.platform_fee_credit_card) }}</span>
                            <span>&middot;</span>
                            <span>Fixa {{ formatFixedFee(tier.platform_fixed_fee) }}</span>
                            <span>&middot;</span>
                            <span>Juros {{ formatFee(tier.installment_fee_credit_card) }}</span>
                        </div>
                    </div>
                    <div class="flex items-center gap-2" @click.stop>
                        <button
                            type="button"
                            class="rounded p-1 text-sm text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-300"
                            @click="editTier(tier)"
                        >
                            <font-awesome-icon icon="fa-solid fa-pencil" />
                        </button>
                        <button
                            type="button"
                            class="rounded p-1 text-sm text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                            @click="confirmDelete(tier)"
                        >
                            <font-awesome-icon icon="fa-solid fa-trash" />
                        </button>
                    </div>
                </div>

                <!-- Collapsible content -->
                <div v-show="expandedTiers[tier.id]" class="divide-y divide-gray-100 border-t border-gray-200 dark:divide-gray-700 dark:border-gray-700">

                    <!-- Taxas Fixas -->
                    <div class="px-4 py-3">
                        <div class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400">
                            <font-awesome-icon icon="fa-solid fa-dollar-sign" class="text-emerald-500" />
                            Cobrança Fixa
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">Nacional</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFixedFee(tier.platform_fixed_fee) }}</div>
                            </div>
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">Internacional</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFixedFee(tier.international_fixed_fee) }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Cartão de Crédito -->
                    <div class="px-4 py-3">
                        <div class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400">
                            <font-awesome-icon icon="fa-solid fa-credit-card" class="text-blue-500" />
                            Cartão de Crédito
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">MDR</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.platform_fee_credit_card) }}</div>
                            </div>
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">Juros por parcela</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.installment_fee_credit_card) }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Boleto -->
                    <div class="px-4 py-3">
                        <div class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400">
                            <font-awesome-icon icon="fa-solid fa-barcode" class="text-amber-500" />
                            Boleto
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">MDR</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.platform_fee_bank_slip) }}</div>
                            </div>
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">Juros por parcela</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.installment_fee_bank_slip) }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Pix -->
                    <div class="px-4 py-3">
                        <div class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400">
                            <font-awesome-icon icon="fa-brands fa-pix" class="text-teal-500" />
                            Pix
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">MDR</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.platform_fee_pix) }}</div>
                            </div>
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">Pix Recorrente</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.platform_fee_pix_recurring) }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Internacional -->
                    <div class="px-4 py-3">
                        <div class="mb-2 flex items-center gap-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400">
                            <font-awesome-icon icon="fa-solid fa-globe" class="text-purple-500" />
                            Pagamento Internacional
                        </div>
                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <span class="text-xs text-gray-500 dark:text-gray-400">MDR</span>
                                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ formatFee(tier.international_platform_fee) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="mx-4 mb-4 mt-3 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
            <p class="text-xs text-blue-800 dark:text-blue-300">
                <font-awesome-icon
                    icon="fa-solid fa-info-circle"
                    class="mr-1"
                />
                Campos em branco (—) utilizam a taxa padrão do seller. Valor
                <strong>0</strong> significa isenção. Quando o valor de uma
                transação se encaixa em uma faixa, as taxas da faixa têm
                <strong>prioridade máxima</strong>.
            </p>
        </div>

        <!-- History section -->
        <div class="border-t border-gray-200">
            <button
                type="button"
                class="flex w-full items-center justify-between px-4 py-3 text-left transition-colors hover:bg-gray-50"
                @click="expandedHistory = !expandedHistory"
            >
                <div class="flex items-center space-x-2">
                    <span class="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100">
                        <font-awesome-icon icon="fa-solid fa-clock-rotate-left" class="text-xs text-violet-600" />
                    </span>
                    <span class="text-sm font-semibold text-gray-700">Histórico de Faixas de Taxas</span>
                    <span v-if="!historyLoading" class="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                        {{ history.length }} {{ history.length === 1 ? 'registro' : 'registros' }}
                    </span>
                </div>
                <font-awesome-icon
                    icon="fa-solid fa-chevron-down"
                    class="text-xs text-gray-400 transition-transform duration-200"
                    :class="{ 'rotate-180': expandedHistory }"
                />
            </button>

            <div v-show="expandedHistory" class="border-t border-gray-200">
                <ticto-loading v-if="historyLoading" class="p-4" />

                <div v-else-if="history.length === 0" class="p-4">
                    <p class="text-sm text-gray-500">Nenhum registro de alteração encontrado.</p>
                </div>

                <div v-else class="p-4">
                    <ol class="relative ml-3 border-l border-gray-200">
                        <li
                            v-for="(entry, idx) in history"
                            :key="idx"
                            class="mb-8 ml-4 last:mb-0"
                        >
                            <div
                                class="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white"
                                :class="{
                                    'bg-green-500': entry.properties?.action === 'create',
                                    'bg-blue-500': entry.properties?.action === 'update',
                                    'bg-red-500': entry.properties?.action === 'delete',
                                    'bg-gray-400': !entry.properties?.action,
                                }"
                            ></div>
                            <div class="flex items-start justify-between">
                                <div>
                                    <time class="text-xs font-normal text-gray-400">
                                        {{ entry.created_at }}
                                    </time>
                                    <p class="mt-0.5 text-sm font-semibold text-gray-900">
                                        {{ entry.observation }}
                                    </p>
                                    <p class="mt-0.5 text-xs text-gray-500">
                                        por {{ entry.user?.name }} ({{ entry.user?.email }})
                                    </p>
                                </div>
                                <button
                                    v-if="entry.properties?.old || entry.properties?.new"
                                    type="button"
                                    class="ml-2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                                    @click="openHistoryDetail(entry)"
                                >
                                    <font-awesome-icon
                                        icon="fa-solid fa-circle-info"
                                        class="h-4 w-4"
                                    />
                                </button>
                            </div>
                        </li>
                    </ol>
                </div>
            </div>
        </div>

        </div><!-- /v-show expandedSection -->

        <!-- History detail modal -->
        <transition-root as="template" :show="showHistoryDetail">
            <dialog-headless
                as="div"
                class="fixed inset-0 z-10 overflow-y-auto"
                @close="showHistoryDetail = false"
            >
                <div class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
                    <transition-child
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <dialog-overlay class="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" />
                    </transition-child>
                    <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                    <transition-child
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div class="inline-block max-h-[80vh] transform overflow-y-auto rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all dark:bg-gray-700 sm:my-8 sm:w-full sm:max-w-lg sm:p-6 sm:align-middle">
                            <dialog-title as="h3" class="mb-4 border-b border-gray-200 pb-2 text-lg font-medium leading-6 dark:text-white">
                                Detalhes da Alteração
                            </dialog-title>

                            <div v-if="historyDetail" class="space-y-4">
                                <div class="text-sm text-gray-600 dark:text-gray-300">
                                    <p><strong>Ação:</strong> {{ historyActionLabel(historyDetail.properties?.action) }}</p>
                                    <p><strong>Data:</strong> {{ historyDetail.created_at }}</p>
                                    <p><strong>Operador:</strong> {{ historyDetail.user?.name }} ({{ historyDetail.user?.email }})</p>
                                </div>

                                <!-- Old values -->
                                <div v-if="historyDetail.properties?.old">
                                    <h4 class="mb-2 text-sm font-semibold text-red-600">Valores Anteriores</h4>
                                    <div class="rounded-lg bg-red-50 p-3 dark:bg-red-900/20">
                                        <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                            <template v-for="(value, key) in historyFilterFields(historyDetail.properties.old)" :key="'old-' + key">
                                                <dt class="text-gray-500">{{ historyFieldLabel(key) }}</dt>
                                                <dd class="font-mono text-gray-800 dark:text-gray-200">{{ historyFormatValue(key, value) }}</dd>
                                            </template>
                                        </dl>
                                    </div>
                                </div>

                                <!-- New values -->
                                <div v-if="historyDetail.properties?.new">
                                    <h4 class="mb-2 text-sm font-semibold text-green-600">Valores Novos</h4>
                                    <div class="rounded-lg bg-green-50 p-3 dark:bg-green-900/20">
                                        <dl class="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                            <template v-for="(value, key) in historyFilterFields(historyDetail.properties.new)" :key="'new-' + key">
                                                <dt class="text-gray-500">{{ historyFieldLabel(key) }}</dt>
                                                <dd class="font-mono text-gray-800 dark:text-gray-200">{{ historyFormatValue(key, value) }}</dd>
                                            </template>
                                        </dl>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-5 flex justify-end">
                                <button
                                    type="button"
                                    class="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                                    @click="showHistoryDetail = false"
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    </transition-child>
                </div>
            </dialog-headless>
        </transition-root>

        <!-- Delete confirmation modal -->
        <transition-root as="template" :show="showDeleteConfirm">
            <dialog-headless
                as="div"
                class="fixed inset-0 z-10 overflow-y-auto"
                @close="showDeleteConfirm = false"
            >
                <div
                    class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
                >
                    <transition-child
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <dialog-overlay
                            class="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
                        />
                    </transition-child>
                    <span
                        class="hidden sm:inline-block sm:h-screen sm:align-middle"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <transition-child
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left align-bottom shadow-xl transition-all dark:bg-gray-700 sm:my-8 sm:w-full sm:max-w-sm sm:p-6 sm:align-middle"
                        >
                            <div class="text-center">
                                <font-awesome-icon
                                    icon="fa-solid fa-triangle-exclamation"
                                    class="text-3xl text-red-500"
                                />
                                <dialog-title
                                    as="h3"
                                    class="mt-3 text-lg font-medium leading-6 dark:text-white"
                                >
                                    Excluir faixa de taxa
                                </dialog-title>
                                <p
                                    class="mt-2 text-sm text-gray-500 dark:text-gray-400"
                                >
                                    Deseja realmente excluir a faixa
                                    <strong>{{
                                        deletingTier
                                            ? formatCurrency(
                                                  deletingTier.min_amount,
                                              ) +
                                              " — " +
                                              formatCurrency(
                                                  deletingTier.max_amount,
                                              )
                                            : ""
                                    }}</strong
                                    >?
                                </p>
                            </div>
                            <div class="mt-5 sm:flex sm:flex-row-reverse">
                                <button
                                    type="button"
                                    class="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                                    @click="deleteTier()"
                                >
                                    Excluir
                                </button>
                                <button
                                    type="button"
                                    class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                                    @click="showDeleteConfirm = false"
                                >
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </transition-child>
                </div>
            </dialog-headless>
        </transition-root>

        <!-- Add/Edit modal -->
        <transition-root as="template" :show="showFormModal">
            <dialog-headless
                as="div"
                class="fixed inset-0 z-10 overflow-y-auto"
                @close="closeFormModal()"
            >
                <div
                    class="flex min-h-screen items-end justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0"
                >
                    <transition-child
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <dialog-overlay
                            class="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
                        />
                    </transition-child>
                    <span
                        class="hidden sm:inline-block sm:h-screen sm:align-middle"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <transition-child
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block max-h-[90vh] transform overflow-y-auto rounded-lg bg-white px-4 pb-4 pt-3 text-left align-bottom shadow-xl transition-all dark:bg-gray-700 sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
                        >
                            <form @submit.prevent="submitForm()">
                                <div class="sm:flex sm:items-start">
                                    <div
                                        class="w-full text-center sm:text-left"
                                    >
                                        <dialog-title
                                            as="h3"
                                            class="mb-4 border-b border-gray-600 pb-2 text-lg font-medium leading-6 dark:text-white"
                                        >
                                            {{
                                                editingTier ? "Editar" : "Nova"
                                            }}
                                            Faixa de Taxa
                                        </dialog-title>

                                        <div class="mt-2">
                                            <!-- Range -->
                                            <div
                                                class="space-x-2 text-sm font-bold uppercase text-gray-900 dark:text-gray-300"
                                            >
                                                <font-awesome-icon
                                                    icon="fa-solid fa-arrows-left-right"
                                                />
                                                Faixa de Valor
                                            </div>
                                            <div class="row flex">
                                                <div class="col mr-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Valor mínimo
                                                    </label>
                                                    <money-input
                                                        v-model="
                                                            form.min_amount
                                                        "
                                                        :cents="true"
                                                        class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div class="col ml-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Valor máximo
                                                    </label>
                                                    <money-input
                                                        v-model="
                                                            form.max_amount
                                                        "
                                                        :cents="true"
                                                        class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                    />
                                                </div>
                                            </div>

                                            <div
                                                class="my-5 flex-grow border-t border-gray-400"
                                            ></div>

                                            <!-- Fixed Fees -->
                                            <div
                                                class="space-x-2 text-sm font-bold uppercase text-gray-900 dark:text-gray-300"
                                            >
                                                <font-awesome-icon
                                                    icon="fa-solid fa-dollar-sign"
                                                />
                                                Cobrança Fixa
                                            </div>
                                            <div class="row flex">
                                                <div class="col mr-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Taxa fixa nacional
                                                    </label>
                                                    <money-input
                                                        v-model="
                                                            form.platform_fixed_fee
                                                        "
                                                        :cents="true"
                                                        class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                    />
                                                </div>
                                                <div class="col ml-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Taxa fixa internacional
                                                    </label>
                                                    <money-input
                                                        v-model="
                                                            form.international_fixed_fee
                                                        "
                                                        :cents="true"
                                                        class="rounded-lg border border-gray-300 bg-gray-50 p-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                    />
                                                </div>
                                            </div>

                                            <div
                                                class="my-5 flex-grow border-t border-gray-400"
                                            ></div>

                                            <!-- Credit Card -->
                                            <div
                                                class="space-x-2 text-sm font-bold uppercase text-gray-900 dark:text-gray-300"
                                            >
                                                <font-awesome-icon
                                                    icon="fa-solid fa-credit-card"
                                                />
                                                Cartão de Crédito
                                            </div>
                                            <div class="row flex">
                                                <div class="col mr-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Taxa da plataforma
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.platform_fee_credit_card
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                                <div class="col ml-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Juros por parcela
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.installment_fee_credit_card
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                class="my-5 flex-grow border-t border-gray-400"
                                            ></div>

                                            <!-- Bank Slip -->
                                            <div
                                                class="space-x-2 text-sm font-bold uppercase text-gray-900 dark:text-gray-300"
                                            >
                                                <font-awesome-icon
                                                    icon="fa-solid fa-barcode"
                                                />
                                                Boleto
                                            </div>
                                            <div class="row flex">
                                                <div class="col mr-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Taxa da plataforma
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.platform_fee_bank_slip
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                                <div class="col ml-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Juros por parcela
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.installment_fee_bank_slip
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                class="my-5 flex-grow border-t border-gray-400"
                                            ></div>

                                            <!-- Pix -->
                                            <div
                                                class="space-x-2 text-sm font-bold uppercase text-gray-900 dark:text-gray-300"
                                            >
                                                <font-awesome-icon
                                                    icon="fa-brands fa-pix"
                                                />
                                                Pix
                                            </div>
                                            <div class="row flex">
                                                <div class="col mr-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Taxa da plataforma
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.platform_fee_pix
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                                <div class="col ml-2 mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Pix Recorrente
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.platform_fee_pix_recurring
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                class="my-5 flex-grow border-t border-gray-400"
                                            ></div>

                                            <!-- International -->
                                            <div
                                                class="space-x-2 text-sm font-bold uppercase text-gray-900 dark:text-gray-300"
                                            >
                                                <font-awesome-icon
                                                    icon="fa-solid fa-globe"
                                                />
                                                Pagamento Internacional
                                            </div>
                                            <div class="row flex">
                                                <div class="col mt-2">
                                                    <label
                                                        class="mb-2 block text-xs font-medium text-gray-900 dark:text-gray-300"
                                                    >
                                                        Taxa da plataforma
                                                    </label>
                                                    <div class="relative">
                                                        <money-input
                                                            v-model="
                                                                form.international_platform_fee
                                                            "
                                                            :options="
                                                                percentOptions
                                                            "
                                                            class="w-full rounded-lg border border-gray-300 bg-gray-50 p-2 pr-8 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
                                                        />
                                                        <span
                                                            class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-sm text-gray-500"
                                                            >%</span
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse"
                                >
                                    <button
                                        type="submit"
                                        :disabled="submitting"
                                        class="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:ml-3 sm:w-auto sm:text-sm"
                                    >
                                        {{
                                            editingTier ? "Atualizar" : "Criar"
                                        }}
                                    </button>
                                    <button
                                        type="button"
                                        class="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                                        @click="closeFormModal()"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </transition-child>
                </div>
            </dialog-headless>
        </transition-root>
    </div>
</template>

<script>
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { useToast } from "vue-toastification";
import MoneyInput from "../../../components/forms/MoneyInput.vue";
import TictoLoading from "../../../components/layouts/TictoLoading.vue";

export default {
    components: {
        DialogHeadless: Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        MoneyInput,
        TictoLoading,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: true,
            submitting: false,
            expandedSection: true,
            tiers: [],
            expandedTiers: {},
            showFormModal: false,
            showDeleteConfirm: false,
            showHistoryDetail: false,
            editingTier: null,
            deletingTier: null,
            historyDetail: null,
            history: [],
            historyLoading: false,
            expandedHistory: false,
            form: this.emptyForm(),
            percentOptions: {
                locale: "pt-BR",
                currency: "BRL",
                currencyDisplay: "hidden",
                valueRange: { min: 0, max: 100 },
                hideCurrencySymbolOnFocus: false,
                hideGroupingSeparatorOnFocus: false,
                hideNegligibleDecimalDigitsOnFocus: false,
                autoDecimalDigits: true,
                useGrouping: false,
                accountingSign: false,
            },
        };
    },
    mounted() {
        this.fetch();
        this.fetchHistory();
    },
    methods: {
        emptyForm() {
            return {
                min_amount: null,
                max_amount: null,
                platform_fee_credit_card: 6.99,
                platform_fee_bank_slip: 6.99,
                platform_fee_pix: 6.99,
                platform_fee_pix_recurring: 6.99,
                international_platform_fee: 8.99,
                installment_fee_credit_card: 3.49,
                installment_fee_bank_slip: 3.49,
                platform_fixed_fee: 249,
                international_fixed_fee: 500,
            };
        },
        fetch() {
            this.loading = true;
            axios
                .get("/api/user/" + this.user.id + "/settings/fee-tiers")
                .then(({ data }) => {
                    this.tiers = data;
                })
                .catch(() => {
                    this.toast.error("Erro ao carregar faixas de taxas.");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        toggleTier(id) {
            this.expandedTiers = { ...this.expandedTiers, [id]: !this.expandedTiers[id] };
        },
        openModal() {
            this.editingTier = null;
            this.form = this.emptyForm();
            this.showFormModal = true;
        },
        editTier(tier) {
            this.editingTier = tier;
            this.form = {
                min_amount: tier.min_amount,
                max_amount: tier.max_amount,
                platform_fee_credit_card: tier.platform_fee_credit_card,
                platform_fee_bank_slip: tier.platform_fee_bank_slip,
                platform_fee_pix: tier.platform_fee_pix,
                platform_fee_pix_recurring: tier.platform_fee_pix_recurring,
                international_platform_fee: tier.international_platform_fee,
                installment_fee_credit_card: tier.installment_fee_credit_card,
                installment_fee_bank_slip: tier.installment_fee_bank_slip,
                platform_fixed_fee: tier.platform_fixed_fee,
                international_fixed_fee: tier.international_fixed_fee,
            };
            this.showFormModal = true;
        },
        closeFormModal() {
            this.showFormModal = false;
            this.editingTier = null;
        },
        submitForm() {
            if (
                this.form.min_amount === null ||
                this.form.max_amount === null
            ) {
                this.toast.error(
                    "Os valores mínimo e máximo são obrigatórios.",
                );
                return;
            }

            if (this.form.min_amount > this.form.max_amount) {
                this.toast.error(
                    "O valor mínimo não pode ser maior que o máximo.",
                );
                return;
            }

            this.submitting = true;

            const payload = {
                min_amount: this.form.min_amount,
                max_amount: this.form.max_amount,
                platform_fee_credit_card: this.form.platform_fee_credit_card,
                platform_fee_bank_slip: this.form.platform_fee_bank_slip,
                platform_fee_pix: this.form.platform_fee_pix,
                platform_fee_pix_recurring:
                    this.form.platform_fee_pix_recurring,
                international_platform_fee:
                    this.form.international_platform_fee,
                installment_fee_credit_card:
                    this.form.installment_fee_credit_card,
                installment_fee_bank_slip: this.form.installment_fee_bank_slip,
                platform_fixed_fee: this.form.platform_fixed_fee,
                international_fixed_fee: this.form.international_fixed_fee,
            };

            const url = "/api/user/" + this.user.id + "/settings/fee-tiers";

            const request = this.editingTier
                ? axios.put(url + "/" + this.editingTier.id, payload)
                : axios.post(url, payload);

            request
                .then(() => {
                    this.toast.success(
                        this.editingTier
                            ? "Faixa atualizada com sucesso!"
                            : "Faixa criada com sucesso!",
                    );
                    this.closeFormModal();
                    this.fetch();
                    this.fetchHistory();
                })
                .catch(({ response }) => {
                    if (response?.data?.errors) {
                        const firstError = Object.values(
                            response.data.errors,
                        )[0];
                        this.toast.error(
                            Array.isArray(firstError)
                                ? firstError[0]
                                : firstError,
                        );
                    } else if (response?.data?.message) {
                        this.toast.error(response.data.message);
                    } else {
                        this.toast.error("Erro ao salvar faixa de taxa.");
                    }
                })
                .finally(() => {
                    this.submitting = false;
                });
        },
        confirmDelete(tier) {
            this.deletingTier = tier;
            this.showDeleteConfirm = true;
        },
        deleteTier() {
            if (!this.deletingTier) return;

            axios
                .delete(
                    "/api/user/" +
                        this.user.id +
                        "/settings/fee-tiers/" +
                        this.deletingTier.id,
                )
                .then(() => {
                    this.toast.success("Faixa excluída com sucesso!");
                    this.showDeleteConfirm = false;
                    this.deletingTier = null;
                    this.fetch();
                    this.fetchHistory();
                })
                .catch(() => {
                    this.toast.error("Erro ao excluir faixa de taxa.");
                    this.showDeleteConfirm = false;
                });
        },
        formatCurrency(cents) {
            if (cents === null || cents === undefined) return "—";
            return (cents / 100).toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
            });
        },
        formatFee(value) {
            if (value === null || value === undefined) return "—";
            return value + "%";
        },
        formatFixedFee(cents) {
            if (cents === null || cents === undefined) return "—";
            return this.formatCurrency(cents);
        },
        fetchHistory() {
            this.historyLoading = true;
            axios
                .get("/api/user/" + this.user.id + "/settings/fee-tiers/history")
                .then(({ data }) => {
                    this.history = data;
                })
                .catch(() => {
                    this.history = [];
                })
                .finally(() => {
                    this.historyLoading = false;
                });
        },
        openHistoryDetail(entry) {
            this.historyDetail = entry;
            this.showHistoryDetail = true;
        },
        historyActionLabel(action) {
            const labels = { create: "Criação", update: "Alteração", delete: "Exclusão" };
            return labels[action] || action;
        },
        historyFieldLabel(key) {
            const labels = {
                min_amount: "Valor mínimo",
                max_amount: "Valor máximo",
                platform_fee_credit_card: "MDR Cartão",
                platform_fee_bank_slip: "MDR Boleto",
                platform_fee_pix: "MDR Pix",
                platform_fee_pix_recurring: "MDR Pix Recorrente",
                international_platform_fee: "MDR Internacional",
                installment_fee_credit_card: "Juros Cartão",
                installment_fee_bank_slip: "Juros Boleto",
                platform_fixed_fee: "Taxa Fixa Nacional",
                international_fixed_fee: "Taxa Fixa Internacional",
            };
            return labels[key] || key;
        },
        historyFilterFields(data) {
            const relevant = [
                "min_amount", "max_amount",
                "platform_fee_credit_card", "platform_fee_bank_slip",
                "platform_fee_pix", "platform_fee_pix_recurring",
                "international_platform_fee",
                "installment_fee_credit_card", "installment_fee_bank_slip",
                "platform_fixed_fee", "international_fixed_fee",
            ];
            const filtered = {};
            for (const key of relevant) {
                if (key in data) filtered[key] = data[key];
            }
            return filtered;
        },
        historyFormatValue(key, value) {
            if (value === null || value === undefined) return "—";
            if (key === "min_amount" || key === "max_amount" || key === "platform_fixed_fee" || key === "international_fixed_fee") {
                return this.formatCurrency(value);
            }
            if (typeof value === "number") return value + "%";
            return value;
        },
    },
};
</script>
